import { useCallback } from 'react';
import { useMutation } from '@common-fe/common-fe';
import { useQueryClient } from '@common-fe/common-fe';

import { api } from '@/api';
import {
  API_URL,
  IVerificationCodeValidationRequestDto,
  IVerificationCodeValidationRequestDtoEmailSourceEnum,
  IVerificationCodeValidationRequestDtoTypeEnum,
  IVerificationCodeValidationResponseDto,
} from '@/dtos';
import { VerificationType } from '@/modules/user/components/Verification/verification.types';
import { QUERY_KEY } from '@/modules/user/queries/useEmployeeViewData.query';

interface VerificationBody {
  code?: string;
  value: string;
  type: VerificationType;
  emailSource?: string;
}

interface VerificationPayload {
  verified?: boolean;
}

const useValidateVerifyCodeQuery = () => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isLoading = false,
    isError,
  } = useMutation((body: VerificationBody) =>
    api.patch<IVerificationCodeValidationResponseDto, IVerificationCodeValidationRequestDto>(
      API_URL.CODES_VERIFICATIONS_VALIDATION,
      {
        code: `${body.code || ''}`,
        value: body.value,
        type: body.type as unknown as IVerificationCodeValidationRequestDtoTypeEnum,
        email_source: body.emailSource as IVerificationCodeValidationRequestDtoEmailSourceEnum,
      }
    )
  );

  const validateVerifyCode = useCallback(
    async (body: VerificationBody) => {
      const response = await mutateAsync(body);
      const responseData = response?.data as unknown as VerificationPayload;
      if (responseData?.verified) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY],
        });
      }
      return responseData;
    },
    [mutateAsync, queryClient]
  );

  return {
    validateVerifyCode,
    isLoading,
    isError,
  };
};

export default useValidateVerifyCodeQuery;
